import { PencilIcon, TrashIcon } from '@heroicons/react/outline';
import { capitalize, Grid, IconButton } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-pro';
import { useCallback, useMemo, useState } from 'react';
import useSWR from 'swr';

import { useDispatch } from 'react-redux';
import { mapGridIndexes, newGridRecordId } from '@shared/commonFunctions';
import { dataGridCol } from '@shared/components/grid/common';
import { gridRowActionsIconProps } from '@shared/components/grid/GridColActions';
import { GridColColor } from '@shared/components/grid/GridColColor';
import { PhoenixGrid } from '@shared/components/grid/PhoenixGrid';
import { AlertModal } from '@shared/components/modals/AlertModal';
import { USER_SERVICE } from '@shared/constants/apiConfig';
import { useTable } from '@shared/hooks/useTable';
import { IRoleUser } from '@shared/interfaces';
import { excludedRowClassName } from '@styles/Admin-tools/Roles/RoleModalStyles';
import { ViewHeightWrapper } from '@styles/CommonStyles';
import { GridColActionsWrapper } from '@styles/PhoenixGrid/GridStyles';
import { RolesContainer } from '@styles/StyledDiv';

import { useAppSelector } from '@store/hooks';
import { selectCurrentState, updateCurrentState } from '@store/reducers/screenStateSlice';
import { IUser } from '../Users/interfaces';
import { IGroupOfUsers } from './interfaces';
import { NewRoleModal } from './NewRole/components/NewRoleModal';
import { requestCreateRole, requestDeleteRole, requestUpdateRole } from './queries';
import { RoleModal } from './RoleModal/RoleModal';
import { IRole, roleType } from './shared';

export const sortUsers = (users: IRoleUser[] = []) => users?.sort((a, b) => (a.userName > b.userName ? 1 : -1));

export interface IAssignments {
  rolesUsers: IRoleUser[];
}

export const roleUsersColumns = [
  dataGridCol('userName', {
    flex: 1.5,
    minWidth: 100,
    headerName: 'User',
  }),
  dataGridCol('assignedFrom', {
    minWidth: 200,
    flex: 3,
  }),
].map((column) => ({ ...column, flex: 1, resizable: false }));

const gridName = 'Admin Role';

export const Roles = () => {
  const { data: allUsers = [], mutate: _mutateAllUsers } = useSWR<IUser[]>('/user-service/users');
  const { data: groupOfUsers } = useSWR<IGroupOfUsers>('/user-service/roleAssignment/groupOfUsers');

  const dispatch = useDispatch();
  const selectedState = useAppSelector(selectCurrentState(gridName));
  const [selectedRole, setSelectedRole] = useState(selectedState ?? '');
  const shouldFetchRoleUsers = selectedRole && selectedRole !== newGridRecordId;
  const { data: roles = [], isValidating, error } = useSWR<IRole[]>(USER_SERVICE.ROLES, {
    revalidateOnMount: true,
  });
  const {
    data: assignments,
    mutate: mutateAssignments,
    isValidating: isAssignmentsValidating,
    error: assigmmentsError
  } = useSWR<IAssignments>(shouldFetchRoleUsers ? `${USER_SERVICE.EDIT_USERS}/${selectedRole}` : null);
  const [roleModalOpen, setRoleModalOpen] = useState(0);
  const [newRoleModalPosition, setNewRoleModalPosition] = useState(0);
  const [currentRole, setCurrentRole] = useState<IRole | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState<string | null>(null);
  const { sortModel, setSortModel } = useTable({ sortModel: [{ field: 'name', sort: 'asc' }] });

  const getRoleName = useCallback(() => {
    return roles?.find((role) => role.id === selectedRole)?.name || '';
  }, [roles, selectedRole]);

  const handleEdit = useCallback((data: IRole) => {
    setCurrentRole(data);
    setNewRoleModalPosition(prevState => prevState + 1);
  }, []);

  const handleEditClick = useCallback((data: IRole) => {
    mutateAssignments();
    handleEdit(data);
  }, [handleEdit]);

  const handleDeleteClick = useCallback((id: string) => {
    requestDeleteRole(id).then(() => {
      setSelectedRole('');
      setShowDeleteModal(null);
    });
  }, []);

  const handleSubmit = useCallback(
    async (data: IRole) => {
      const payload = data.permission
        ? { ...data, name: data.name.trim(), roleType }
        : ({
            name: data.name.trim(),
            roleType,
            commentsColor: data.commentsColor,
            commentsColorPriority: data.commentsColorPriority,
          } as IRole);

      if (data.id) {
        await requestUpdateRole({ ...payload, id: data.id });
      } else {
        requestCreateRole(payload);
      }
      mutateAssignments().then(() => {
        setCurrentRole(null);
        setNewRoleModalPosition(0);
      });
    },
    [mutateAssignments]
  );

  const handleCloseNewRoleModal = useCallback(() => {
    setCurrentRole(null);
    setNewRoleModalPosition(0);
  }, []);

  const onCreateRole = useCallback(() => {
    setCurrentRole({} as IRole);
    setNewRoleModalPosition(prevState => prevState + 1);
  }, []);

  const onSelectionModelChange = useCallback((model: string | number) => {
    model && selectedRole !== `${model}` && setSelectedRole(`${model}`);
    dispatch(updateCurrentState({ screenName: gridName, screenState: model }));
  }, [selectedRole]);

  const rolesColumns: GridColDef[] = useMemo(() => [
    dataGridCol('name', {
      minWidth: 200,
      headerName: 'Role Name ',
    }),
    dataGridCol('users', {
      minWidth: 120,
      editable: false,
    }),
    dataGridCol('commentsColor', {
      minWidth: 150,
      renderCell: (params) => <GridColColor params={params} />,
      renderEditCell: (params) => <GridColColor params={params} />,
    }),
    dataGridCol('commentsColorPriority', {
      minWidth: 120,
      headerName: 'CC Priority',
    }),
    dataGridCol('actions', {
      minWidth: 80,
      sortable: false,
      disableColumnMenu: true,
      renderCell: ({ row }) => (
        <GridColActionsWrapper>
          <IconButton>
            <PencilIcon onClick={() => handleEditClick(row)} {...gridRowActionsIconProps} />
          </IconButton>

          <IconButton>
            <TrashIcon onClick={() => setShowDeleteModal(row?.id)} {...gridRowActionsIconProps} />
          </IconButton>
        </GridColActionsWrapper>
      ),
    }),
  ].map((column) => ({ flex: 1, editable: false, ...column })), [handleEditClick]);

  const rows = useMemo(() => mapGridIndexes(sortUsers(assignments?.rolesUsers)), [assignments?.rolesUsers]);

  return (
    <ViewHeightWrapper>
      <RolesContainer>
        <Grid
          container
          spacing={4}
        >
          <Grid
            item
            lg={selectedRole ? 6 : 12}
            md={selectedRole ? 6 : 12}
            sm={selectedRole ? 6 : 12}
            xs={selectedRole ? 6 : 12}
          >
            <PhoenixGrid
              withHeader
              canNotCopy
              name={gridName}
              buttonTitle="Create New Role"
              height="calc(100% - 50px)"
              pageSize={25}
              columns={rolesColumns}
              loading={(!roles && !error) || isValidating}
              rows={roles && !isValidating ? roles : []}
              sortModel={sortModel}
              selectionModel={(roles && !isValidating) && selectedRole ? [selectedRole] : []}
              onSelectionModelChange={([selectionModel]) => onSelectionModelChange(selectionModel)}
              onButtonClick={onCreateRole}
              onSortModelChange={setSortModel}
            />
          </Grid>

          {selectedRole && (
            <Grid
              item
              lg={6}
              md={6}
              sm={6}
            >
              <PhoenixGrid
                autoHeight
                withHeader
                name="User"
                pageSize={25}
                height="calc(100% - 50px)"
                buttonTitle="Edit Users"
                disabled={!selectedRole}
                columns={roleUsersColumns}
                getRowClassName={excludedRowClassName}
                loading={(!rows && !assigmmentsError) || isAssignmentsValidating}
                rows={rows && !isAssignmentsValidating ? rows : []}
                onButtonClick={() => setRoleModalOpen(prevState => prevState + 1)}
              />
            </Grid>
          )}
        </Grid>

        {!!roleModalOpen && (
          <RoleModal
            roleModalPosition={roleModalOpen}
            roleId={selectedRole}
            roleName={getRoleName()}
            allUsers={allUsers}
            users={assignments?.rolesUsers}
            groupOfUsers={groupOfUsers}
            handleClose={() => {
              setRoleModalOpen(0);
              mutateAssignments();
            }}
          />
        )}

        <NewRoleModal
          open={!!currentRole}
          newRoleModalPosition={newRoleModalPosition}
          handleClose={handleCloseNewRoleModal}
          currentRole={currentRole}
          handleSubmit={handleSubmit}
        />

        {!!showDeleteModal && (
          <AlertModal
            open
            title={`Delete ${capitalize(`${getRoleName()} Role`)}`}
            subtitle={`Are you sure you want to delete this ${capitalize(`${getRoleName()} Role`)}?`}
            onConfirm={() => handleDeleteClick(showDeleteModal)}
            onClose={() => setShowDeleteModal(null)}
          />
        )}
      </RolesContainer>
    </ViewHeightWrapper>
  );
};
